/* Copyright (c) 2025 Xperi. All Rights Reserved. */

/* eslint-disable max-len */
import { GuideProgram } from 'client-code/filtered-guide';

import { DeviceModel } from '../../../../core/device';
import { Channel } from '../../../../core/entities';
import { InteractiveComponents } from '../../../../core/enums';
import { TChannelV2 } from '../../../../core/types';
import { DateTime } from '../../../../core/utils';
import { FILTERED_GUIDE_MAX_SHOWS_PER_PAGE } from '../filteredguide.constants';

export interface IFilteredGuideTableProps {
    category: string;
    channelsType: string;
    allPossibleChannels: Array<TChannelV2 | Channel>;
}

export interface GuidePaginationState {
    pages: Map<number, LimitedGuideProgramResult[]>;
    currentPageIndex: number;
    previousPageIndex: number;
}

export const mapGuideProgramByStartTime = (guidePrograms: GuideProgram[]) => {
    return guidePrograms.reduce(
        (acc, program) => {
            const { startTime } = program;
            if (!acc[startTime]) {
                acc[startTime] = [];
            }
            acc[startTime].push(program);
            return acc;
        },
        {} as Record<number, GuideProgram[]>,
    );
};

export type LimitedGuideProgramResult =
    | { type: 'program'; value: GuideProgram }
    | { type: 'time'; value: number };

export const getLimitedGuideProgramsByStartTime = (
    guidePrograms: GuideProgram[],
): LimitedGuideProgramResult[] => {
    const grouped = mapGuideProgramByStartTime(guidePrograms);
    const sortedEntries = Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b));

    const result: LimitedGuideProgramResult[] = [];

    sortedEntries.forEach(([key, programs]) => {
        result.push({ type: 'time', value: Number(key) });
        programs.forEach((program) => {
            result.push({ type: 'program', value: program });
        });
    });

    return result;
};

export const filterTimeRowsAlreadyExistingInPages = (
    allTimeRows: number[],
    newPages: LimitedGuideProgramResult[],
) => {
    return newPages.filter((item) => {
        if (item.type !== 'time') return true;
        return !allTimeRows.includes(item.value);
    });
};

export const getLastCompletedHour = (): Date => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    return now;
};

export const getLastProgramIndex = (guideProgramList: LimitedGuideProgramResult[]): number => {
    if (guideProgramList.length === 0) {
        return 0;
    }
    const programCount = guideProgramList.filter((item) => item.type === 'program').length ?? 0;
    if (programCount === 0) {
        return 0;
    }
    return programCount - 1;
};

export const getFirstProgramStartTimeFromPage = (guideProgramList: LimitedGuideProgramResult[]) => {
    const firstProgram = guideProgramList.find(
        (item): item is { type: 'program'; value: GuideProgram } => item.type === 'program',
    );
    let firstProgramStartTime = 0;
    if (guideProgramList.length === 1 && guideProgramList[0]?.type === 'time') {
        // if the only item in the page is a timeslot header, we should use its date
        firstProgramStartTime = guideProgramList[0].value;
    } else {
        firstProgramStartTime = firstProgram ? firstProgram.value.startTime : 0;
    }

    return firstProgramStartTime;
};

export const getInfoAboutPageMap = (
    pagesMap: Map<number, LimitedGuideProgramResult[]>,
    goesToFuture: boolean,
) => {
    const lastPageIndex = goesToFuture
        ? Math.max(...pagesMap.keys())
        : Math.min(...pagesMap.keys());
    const lastPage = lastPageIndex ? pagesMap.get(lastPageIndex)! : [];
    const lastPrograms = lastPage
        .filter((item): item is { type: 'program'; value: GuideProgram } => item.type === 'program')
        .map((item) => item.value);
    const offerIds: string[] = [];
    pagesMap.forEach((pageItems) => {
        pageItems.forEach((item) => {
            if (item.type === 'program') {
                offerIds.push((item as { type: 'program'; value: GuideProgram }).value.offerId);
            }
        });
    });
    const existingOfferIds = new Set(offerIds);
    const isLastPageIncomplete = lastPage.length < FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
    const allTimesExceptLastIncompletePage: number[] = [];
    pagesMap.forEach((pageItems, pageIndex) => {
        if (pageIndex !== lastPageIndex || !isLastPageIncomplete) {
            pageItems.forEach((item) => {
                if (item.type === 'time') {
                    allTimesExceptLastIncompletePage.push(
                        (item as { type: 'time'; value: number }).value,
                    );
                }
            });
        }
    });

    return {
        lastPageIndex,
        lastPage,
        lastPrograms,
        existingOfferIds,
        allTimesExceptLastIncompletePage,
    };
};

export const getMappedItemsToAddToPages = (
    allTimeRows: number[],
    programsToMap: GuideProgram[],
) => {
    const mapped = getLimitedGuideProgramsByStartTime(programsToMap);
    return mapped.filter((item) => {
        if (item.type !== 'time') return true;
        return !allTimeRows.includes(item.value);
    });
};
export const updatePagesMap = (
    pagesMap: Map<number, LimitedGuideProgramResult[]>,
    mappedItems: LimitedGuideProgramResult[],
    startPageIndex: number,
    pageSize: number,
) => {
    const totalPages = Math.ceil(mappedItems.length / pageSize);
    for (let i = 0; i < totalPages; i += 1) {
        const start = i * pageSize;
        const end = start + pageSize;
        pagesMap.set(startPageIndex + i, mappedItems.slice(start, end));
    }
};

export const splitIntoPages = (
    items: LimitedGuideProgramResult[],
    startPageIndex: number,
    goesToFuture: boolean,
) => {
    const pagesMap = new Map<number, LimitedGuideProgramResult[]>();
    const totalPages = Math.ceil(items.length / FILTERED_GUIDE_MAX_SHOWS_PER_PAGE);

    for (let i = 0; i < totalPages; i += 1) {
        const start = goesToFuture
            ? i * FILTERED_GUIDE_MAX_SHOWS_PER_PAGE
            : items.length - (i + 1) * FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
        const end = start + FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
        const sliceStart = start < 0 ? 0 : start;
        const pageItems = items.slice(sliceStart, end);

        const pageIndex = goesToFuture ? startPageIndex + i : startPageIndex - i;
        pagesMap.set(pageIndex, pageItems);
    }

    return pagesMap;
};

export const splitInitialPages = (
    beforeItems: LimitedGuideProgramResult[],
    afterItems: LimitedGuideProgramResult[],
): Map<number, LimitedGuideProgramResult[]> => {
    const pagesMap = new Map<number, LimitedGuideProgramResult[]>();

    const beforeCount = Math.ceil(beforeItems.length / FILTERED_GUIDE_MAX_SHOWS_PER_PAGE);
    for (let i = 0; i < beforeCount; i += 1) {
        const start = beforeItems.length - (i + 1) * FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
        const end = start + FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
        const sliceStart = start < 0 ? 0 : start;
        const pageItems = beforeItems.slice(sliceStart, end);
        pagesMap.set(-1 - i, pageItems);
    }

    const afterCount = Math.ceil(afterItems.length / FILTERED_GUIDE_MAX_SHOWS_PER_PAGE);
    for (let i = 0; i < afterCount; i += 1) {
        const start = i * FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
        const end = start + FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
        pagesMap.set(i, afterItems.slice(start, end));
    }

    return pagesMap;
};

/**
 * Returns the index of the first item in the mapped array that has a start time greater than or equal to the given startTimestamp.
 *
 * If the current time is past 30 minutes, it checks for a specific program that starts exactly at hh:30.
 * If such a program exists, its index is returned. Otherwise, it falls back to the default behavior.
 *
 * The default behavior returns the index of the first item in the array that has a start time
 * greater than or equal to the provided startTimestamp (which is always aligned to hh:00).
 *
 * @param {Date} startTimestamp - The base timestamp, always aligned to the start of the hour (hh:00).
 * @param {Array} mapped - The array of guide items, each with a `type` and a Unix `value` in seconds.
 * @returns {number} The index of the appropriate start time item in the mapped array.
 */
export const getStartIndexFromTimestamp = (
    startTimestamp: Date,
    mapped: LimitedGuideProgramResult[],
    skipCurrentTimeCheck: boolean = false,
) => {
    const now = new Date();

    if (now.getMinutes() >= 30 && !skipCurrentTimeCheck) {
        const thirtyStart = new Date(startTimestamp);
        thirtyStart.setMinutes(30, 0, 0);

        const hasThirtyProgram = mapped.some(
            (item) =>
                item.type === 'time' &&
                new Date(item.value * 1000).getTime() === thirtyStart.getTime(),
        );

        if (hasThirtyProgram) {
            return mapped.findIndex(
                (item) =>
                    item.type === 'time' &&
                    new Date(item.value * 1000).getTime() === thirtyStart.getTime(),
            );
        }
    }

    return mapped.findIndex(
        (item) => item.type === 'time' && new Date(item.value * 1000) >= startTimestamp,
    );
};

export const getTurboScrollTimeText = (currentTurboScrollDate: Date) => {
    const dateTime = new DateTime(currentTurboScrollDate.getTime());

    const day = dateTime
        .toLocaleDayWithDate(DeviceModel.getTVProperties().getLang())
        .replace('.', '');
    const time = dateTime.to12HourClock();

    return `${day} ${time}`;
};

export const getNextTurboScrollTimeVallue = (
    currentTurboScrollDate: Date,
    direction: InteractiveComponents,
    minStartTimestamp: number,
    maxStartTimestamp: number,
) => {
    const offset = direction === InteractiveComponents.NORTH ? -4 : 4;
    const newDate = new Date(currentTurboScrollDate.getTime() + offset * 60 * 60 * 1000);
    const hasReachedBounds =
        newDate.getTime() < minStartTimestamp * 1000 ||
        newDate.getTime() > maxStartTimestamp * 1000;

    if (hasReachedBounds && direction === InteractiveComponents.NORTH) {
        return new Date(minStartTimestamp * 1000);
    }
    if (hasReachedBounds && direction === InteractiveComponents.SOUTH) {
        return new Date(maxStartTimestamp * 1000);
    }
    return newDate;
};
